import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  deleteUser,
  updateProfile
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from './config';
import { handleError } from '../utils/errorHandler';

// Create a new user with email and password
export const registerUser = async (email, password, fullName) => {
  let user = null;
  try {
    // 1. First create the user in Firebase Auth
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    user = userCredential.user;

    // 1.1 Set displayName in Firebase Auth so UI fallbacks never show "User"
    const safeFullName = (fullName || '').trim();
    if (safeFullName) {
      await updateProfile(user, { displayName: safeFullName });
    }
    
    // 2. Prepare user data with proper balance structure
    const userData = {
      uid: user.uid,
      email: email.toLowerCase().trim(),
      fullName: safeFullName || user.displayName || 'User',
      isAdmin: false,
      canWin: true,
      balance: 0,
      // Legacy balance structure (kept for backward compatibility)
      cryptoBalances: {
        USDT: 0,
        USDC: 0,
        BTC: 0,
        ETH: 0,
        SOL: 0,
        XRP: 0
      },
      // New balance structure used by BalanceContext
      accountBalances: {
        funding: {},
        trading: {},
        arbitrage: {}
      },
      transactions: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      verification_status: 'unverified'
    };
    
    // 3. Save to Firestore
    const userProfileRef = doc(db, 'userProfiles', user.uid);
    await setDoc(userProfileRef, userData);

    // Also mirror to users/{uid} since other parts of the app still read/write there.
    const usersRef = doc(db, 'users', user.uid);
    await setDoc(usersRef, userData, { merge: true });
    
    return { 
      success: true, 
      user: {
        uid: user.uid,
        email: user.email,
        emailVerified: user.emailVerified
      }
    };
    
  } catch (error) {
    console.error('Registration error:', {
      code: error.code,
      message: error.message,
      email: email,
      stack: error.stack
    });
    
    // Clean up auth user if Firestore write failed
    if (user && user.uid) {
      try {
        await deleteUser(user);
        console.log('Cleaned up auth user after failed registration');
      } catch (deleteError) {
        console.error('Failed to clean up auth user:', deleteError);
      }
    }
    
    // More specific error messages based on error code
    let errorMessage = 'Registration failed. Please try again.';
    switch(error.code) {
      case 'auth/email-already-in-use':
        errorMessage = 'This email is already registered. Please sign in instead.';
        break;
      case 'auth/invalid-email':
        errorMessage = 'Please enter a valid email address.';
        break;
      case 'auth/weak-password':
        errorMessage = 'Password should be at least 6 characters long.';
        break;
      case 'permission-denied':
        errorMessage = 'Permission denied. Please contact support.';
        break;
      default:
        errorMessage = error.message || 'An unknown error occurred. Please try again.';
        break;
    }
    
    return { 
      success: false, 
      error: handleError(error, errorMessage),
      code: error.code
    };
  }
};

// Sign in an existing user
export const loginUser = async (email, password) => {
  try {
    console.log('Attempting to sign in with email:', email);
    
    // Validate email and password
    if (!email || !password) {
      throw new Error('Email and password are required');
    }
    
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    console.log('Sign in successful for user:', userCredential.user.uid);
    
    return { 
      success: true, 
      user: userCredential.user 
    };
  } catch (error) {
    console.error('Error signing in:', {
      code: error.code,
      message: error.message,
      email: email,
      timestamp: new Date().toISOString()
    });
    
    let errorMessage = 'Sign in failed. Please check your credentials and try again.';
    
    // More specific error messages
    switch (error.code) {
      case 'auth/user-not-found':
        errorMessage = 'No user found with this email address.';
        break;
      case 'auth/wrong-password':
        errorMessage = 'Incorrect password. Please try again.';
        break;
      case 'auth/too-many-requests':
        errorMessage = 'Too many failed attempts. Please try again later.';
        break;
      case 'auth/invalid-email':
        errorMessage = 'Please enter a valid email address.';
        break;
      case 'auth/invalid-credential':
        errorMessage = 'Invalid login credentials. Please check your email and password.';
        break;
      default:
        errorMessage = `Authentication error: ${error.message}`;
        break;
    }
    
    return { 
      success: false, 
      error: errorMessage,
      code: error.code
    };
  }
};

// Sign out the current user
export const logoutUser = async () => {
  try {
    await signOut(auth);
    return { success: true };
  } catch (error) {
    console.error('Error signing out:', error);
    return { success: false, error: error.message };
  }
};

// Get the current user
export const getCurrentUser = () => {
  return auth.currentUser;
};

// Subscribe to auth state changes
export const onAuthChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

// Check if a user exists in Firestore
export const checkUserExists = async (uid) => {
  try {
    const userDoc = await getDoc(doc(db, 'userProfiles', uid));
    return userDoc.exists();
  } catch (error) {
    console.error('Error checking if user exists:', error);
    return false;
  }
};

// Ensure a user document exists in Firestore
export const ensureUserDocument = async (user) => {
  try {
    const userRef = doc(db, 'userProfiles', user.uid);
    const userSnap = await getDoc(userRef);
    
    if (!userSnap.exists()) {
      // Create default user document if it doesn't exist
      await setDoc(userRef, {
        email: user.email,
        fullName: user.displayName || 'User',
        isAdmin: false,
        canWin: true,
        balance: 0,
        // Legacy balance structure
        cryptoBalances: {
          USDT: 0,
          USDC: 0,
          BTC: 0,
          ETH: 0,
          SOL: 0,
          XRP: 0
        },
        // New balance structure
        accountBalances: {
          funding: {},
          trading: {},
          arbitrage: {}
        },
        transactions: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        verification_status: 'unverified'
      });
      return true;
    }
    return false; // Document already existed
  } catch (error) {
    console.error('Error ensuring user document:', error);
    return false;
  }
};

const authService = {
  registerUser,
  loginUser,
  logoutUser,
  getCurrentUser,
  onAuthChange,
  checkUserExists,
  ensureUserDocument
};

export default authService;
